/**
 * The sheet element's size tracking: a ResizeObserver size, deduplicated and
 * clamped against a repeating A→B→A oscillation, resolving the size and
 * layout gates (`SheetElement.onSizeChange`); and the seed from a direct
 * measurement when no notification arrived. Moved out of sheet-element.ts
 * (r5 FS 2). This module owns `SheetSizeState`: it is the only writer of the
 * element's size.
 */
import type { Option } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import type { Size } from "@tento-ui/layout-utils";
import type { SizeGate } from "@tento-ui/ui/size-gate";
import type { SheetLayoutGate } from "./sheet-layout-gate.ts";
import type { SheetElementCore } from "./sheet-element-core.ts";

// Clamp an A→B→A size oscillation only after it repeats this many times — a
// sustained layout feedback loop, not a single user resize-back. See
// `SheetElement`'s size handling.
const OSCILLATION_CLAMP_LIMIT = 2;

/** The element's accepted size and the oscillation check's memory. Owner: this module. */
export class SheetSizeState {
  /** The element's last accepted size (`SheetElement.size`). */
  size: Option<Size> = null;
  /** The size before it, for the oscillation check. */
  prevSize: Option<Size> = null;
  /** Consecutive A→B→A hits. */
  oscillationCount = 0;
}

/** What size tracking reads on its sheet element, and the state it writes. */
export interface SizeHost {
  /** The element as every step reads it (its box and connection). */
  readonly core: SheetElementCore;
  /** The size state; written here only. */
  readonly state: SheetSizeState;
  /** Resolved with the first renderable size. */
  readonly sizeGate: SizeGate;
  /** Takes the viewport width. */
  readonly layoutGate: SheetLayoutGate;
  /** Height-only resizes change body residency. */
  refreshRowViewport(): void;
  /** The element's size hook (`SheetElement.onSizeChange`). */
  readonly onSizeChange: Option<(s: Size) => void>;
}

/** Take `s` as the element's size (see the module note). */
export function applySizeChange(host: SizeHost, s: Size): void {
  const state = host.state;
  const element = host.core.element;
  const width = Math.round(s.width || element.offsetWidth);
  const height = Math.round(s.height || element.offsetHeight);

  // Carousel/tab hosts can connect a sheet while it is display:none. A 0x0
  // ResizeObserver notification is not a renderable sheet size; letting it
  // resolve SizeGate causes the first bind to build a zero-width viewport.
  if (width <= 0 || height <= 0) return;

  // Skip if size hasn't actually changed (avoids subpixel ResizeObserver noise)
  if (state.size && state.size.width === width && state.size.height === height) {
    return;
  }

  // A genuine layout feedback loop flips between exactly two sizes every
  // frame (e.g. a scrollbar appearing/disappearing nudges the width back and
  // forth). A user resizing the window back to a prior width is a SINGLE such
  // match and MUST be honored — otherwise applyLayout never re-runs and the
  // cells keep their stale (narrow) width instead of re-stretching on
  // grow-back. So only clamp once the oscillation REPEATS, not on the first
  // match; any distinct new size resets the counter (a real drag is a stream
  // of distinct sizes, never an exact repeat).
  const isOscillation =
    state.prevSize != null &&
    state.prevSize.width === width &&
    state.prevSize.height === height;
  if (isOscillation) {
    state.oscillationCount += 1;
    if (state.oscillationCount >= OSCILLATION_CLAMP_LIMIT) return;
  } else {
    state.oscillationCount = 0;
  }
  state.prevSize = state.size;

  Tracing.info("ui")("core/sheet/size", { width, height });
  state.size = { width, height };
  host.sizeGate.resolve(state.size);
  host.layoutGate.setViewportWidth(width);
  // Height-only resizes change body residency even when columns keep their width.
  host.refreshRowViewport();
}

/**
 * Seed `SizeGate` from a direct measurement when no ResizeObserver
 * notification has arrived yet.
 *
 * `SizeGate` is a notification channel for a value the element can report at
 * any time — `onSizeChange` already falls back to `offsetWidth`/`offsetHeight`
 * when the observer hands it a zero. But `bindSheet` awaited the gate with no
 * other way to fill it, so a missing notification was an unbounded wait: the
 * bind stops before `rebindLegend`, `layoutGate` never gets a legend width,
 * and the restoring mutate that re-reveals content never runs, leaving the
 * grid hidden with `legend-ready` off.
 *
 * ResizeObserver callbacks are delivered in the "update the rendering" step,
 * so a document that is not being rendered gets none — measured on
 * `dev--calendar` in a background tab: the host reported 618×989 to
 * `getBoundingClientRect` while `host.size` was still unset and the render
 * promise had been pending for minutes.
 *
 * Reading `offsetWidth` forces layout, which is what the observer would have
 * measured anyway; it runs once per bind and only while `size` is unset, so
 * the steady state is untouched. The zero guard is deliberately the same one
 * `onSizeChange` applies: a sheet connected under `display: none` measures
 * 0×0 and MUST stay unresolved rather than bind a zero-width viewport.
 *
 * This removes one unbounded wait. It does **not** make a non-rendering
 * document complete a bind — `rebindLegend`'s measurement promises and the
 * final `$$.mutate` are both fastdom (rAF) work, and a hidden tab has no
 * frames. Automation needs a context that actually paints; see the note on
 * `__dispatchApp`.
 */
export function seedSizeFromMeasurement(host: SizeHost): void {
  if (host.state.size != null) return;
  const element = host.core.element;
  if (!element.isConnected) return;
  const width = Math.round(element.offsetWidth);
  const height = Math.round(element.offsetHeight);
  if (width <= 0 || height <= 0) return;
  host.onSizeChange?.({ width, height });
}
